import { Component, OnInit, ChangeDetectorRef, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { RideService } from '../rideService';
import { CityService, City } from '../../cities/cityService';
import { UserService } from '../../users/userService';
import { CreateRideDto } from '../dto/createRideDto';
import { ListRideDto } from '../dto/listRideDto';
import { RestResponse } from '../../../shared/RestResponse';
import { GetRideDto } from '../dto/getRideDto';

@Component({
  selector: 'app-ride-form',
  templateUrl: './ride-form.html',
  imports: [
    CommonModule, 
    FormsModule, 
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    CheckboxModule,
    MessageModule,
    ProgressSpinnerModule,
    DividerModule,
    SelectModule
  ]
})
export class RideFormComponent implements OnInit, AfterViewInit, OnDestroy {
  rideForm!: FormGroup;
  isLoading: boolean = false;
  errorMessage: string = '';
  isEditMode: boolean = false;
  rideId: number | null = null;
  fromCities: any[] = [];
  toCities: any[] = [];
  drivers: any[] = [];
  isLoadingFromCities: boolean = false;
  isLoadingToCities: boolean = false;
  isLoadingDrivers: boolean = false;
  
  private driverSearchSubject = new Subject<string>();
  private fromCitySearchSubject = new Subject<string>();
  private toCitySearchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private rideService: RideService,
    private cityService: CityService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadCities();
    this.loadDrivers();
    
    // Configurar busca de motoristas com debounce
    this.driverSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(search => {
        this.isLoadingDrivers = true;
        // Se não houver busca, carregar todos
        return this.userService.list(search || '', 1, null, null, undefined, [4]);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: RestResponse<any[]>) => {
        // Substituir completamente o array - a API já retorna os resultados filtrados
        this.drivers = response.data || [];
        this.cdr.detectChanges();
        this.isLoadingDrivers = false;
      },
      error: (error) => {
        console.error('Erro ao buscar motoristas:', error);
        this.drivers = [];
        this.isLoadingDrivers = false;
      }
    });
    
    // Configurar busca de cidade de origem com debounce
    this.fromCitySearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(search => {
        this.isLoadingFromCities = true;
        // Se não houver busca, carregar todas
        return this.cityService.list(search || '');
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: RestResponse<City[]>) => {
        // Atualizar apenas o array de cidades de origem
        this.fromCities = (response.data || []).map(city => ({
          ...city,
          displayName: `${city.name} - ${city.stateName}`
        }));
        this.cdr.detectChanges();
        this.isLoadingFromCities = false;
      },
      error: (error) => {
        console.error('Erro ao buscar cidades de origem:', error);
        this.fromCities = [];
        this.isLoadingFromCities = false;
      }
    });
    
    // Configurar busca de cidade de destino com debounce
    this.toCitySearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(search => {
        this.isLoadingToCities = true;
        // Se não houver busca, carregar todas
        return this.cityService.list(search || '');
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: RestResponse<City[]>) => {
        // Atualizar apenas o array de cidades de destino
        this.toCities = (response.data || []).map(city => ({
          ...city,
          displayName: `${city.name} - ${city.stateName}`
        }));
        this.cdr.detectChanges();
        this.isLoadingToCities = false;
      },
      error: (error) => {
        console.error('Erro ao buscar cidades de destino:', error);
        this.toCities = [];
        this.isLoadingToCities = false;
      }
    });
    
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.rideId = +params['id'];
        this.isEditMode = true;
        this.loadRide();
      }
    });
    
    // Força a detecção de mudanças para garantir que os estilos sejam aplicados
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
    
    // Força novamente após um pequeno delay para garantir que os estilos sejam aplicados
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // Força a detecção de mudanças após a view ser inicializada
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  private initializeForm(): void {
    this.rideForm = this.fb.group({
      driverId: [0, [Validators.required, Validators.min(1)]],
      date: ['', [Validators.required]],
      fromCityId: [0, [Validators.required, Validators.min(1)]],
      toCityId: [0, [Validators.required, Validators.min(1)]],
    });
  }

  private loadCities(): void {
    this.isLoadingFromCities = true;
    this.isLoadingToCities = true;
    this.cityService.list().subscribe({
      next: (response: RestResponse<City[]>) => {
        const cities = response.data.map(city => ({
          ...city,
          displayName: `${city.name} - ${city.stateName}`
        }));
        this.fromCities = [...cities];
        this.toCities = [...cities];
        this.isLoadingFromCities = false;
        this.isLoadingToCities = false;
      },
      error: (error) => {
        console.error('Erro ao carregar cidades:', error);
        this.fromCities = [];
        this.toCities = [];
        this.isLoadingFromCities = false;
        this.isLoadingToCities = false;
      }
    });
  }

  private loadDrivers(): void {
    this.isLoadingDrivers = true;
    this.userService.list('', 1, null, null, undefined, [4]).subscribe({
      next: (response: RestResponse<any[]>) => {
        this.drivers = response.data;
        this.isLoadingDrivers = false;
      },
      error: (error) => {
        console.error('Erro ao carregar motoristas:', error);
        this.drivers = [];
        this.isLoadingDrivers = false;
      }
    });
  }
  
  onDriverFilter(event: any): void {
    const search = event.filter || '';
    // Disparar busca na API
    this.driverSearchSubject.next(search);
  }
  
  onFromCityFilter(event: any): void {
    const search = event.filter || '';
    // Disparar busca na API
    this.fromCitySearchSubject.next(search);
  }
  
  onToCityFilter(event: any): void {
    const search = event.filter || '';
    // Disparar busca na API
    this.toCitySearchSubject.next(search);
  }

  private loadRide(): void {
    if (this.rideId) {
      this.isLoading = true;
      this.rideService.getById(this.rideId).subscribe({
        next: (response: RestResponse<GetRideDto>) => {
          this.rideForm.patchValue({
            driverId: response.data.driverId,
            date: new Date(response.data.date),
            fromCityId: response.data.fromCityId,
            toCityId: response.data.toCityId,
          });
          this.isLoading = false;
          
          // Força a detecção de mudanças após carregar os dados
          this.cdr.detectChanges();
          
          // Força novamente após um pequeno delay para garantir que os estilos sejam aplicados
          setTimeout(() => {
            this.cdr.detectChanges();
          }, 50);
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = 'Erro ao carregar dados da corrida.';
          console.error('Erro ao carregar corrida:', error);
        }
      });
    }
  }

  onSubmit(): void {
    if (this.rideForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';

      const formValue = this.rideForm.value;
      let dateValue: string;
      
      // Converter data para ISO string
      if (formValue.date instanceof Date) {
        dateValue = formValue.date.toISOString();
      } else if (typeof formValue.date === 'string') {
        // Se já for string, tentar converter
        dateValue = new Date(formValue.date).toISOString();
      } else {
        this.isLoading = false;
        this.errorMessage = 'Data inválida.';
        return;
      }

      const rideData: CreateRideDto = {
        driverId: formValue.driverId,
        date: dateValue,
        fromCityId: formValue.fromCityId,
        toCityId: formValue.toCityId,
        isDelivery: formValue.isDelivery || false
      };

      console.log('Enviando dados:', rideData);

      if (this.isEditMode && this.rideId) {
        this.updateRide(rideData);
      } else {
        this.createRide(rideData);
      }
    } else {
      this.markFormGroupTouched();
      console.log('Formulário inválido:', this.rideForm.errors);
      console.log('Erros nos campos:', {
        driverId: this.rideForm.get('driverId')?.errors,
        date: this.rideForm.get('date')?.errors,
        fromCityId: this.rideForm.get('fromCityId')?.errors,
        toCityId: this.rideForm.get('toCityId')?.errors
      });
    }
  }

  private createRide(rideData: CreateRideDto): void {
    console.log('Criando corrida com dados:', rideData);
    this.rideService.create(rideData).subscribe({
      next: (response: RestResponse<void>) => {
        console.log('Corrida criada com sucesso:', response);
        this.isLoading = false;
        this.router.navigate(['/rides']);
      },
      error: (error) => {
        console.error('Erro ao criar corrida:', error);
        this.isLoading = false;
        if (error.error?.message) {
          this.errorMessage = error.error.message;
        } else {
          this.errorMessage = 'Erro ao cadastrar corrida. Tente novamente.';
        }
      }
    });
  }

  private updateRide(rideData: CreateRideDto): void {
    if (this.rideId) {
      this.rideService.update(this.rideId, rideData).subscribe({
        next: (response: RestResponse<CreateRideDto>) => {
          this.isLoading = false;
          this.router.navigate(['/rides']);
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = 'Erro ao atualizar corrida. Tente novamente.';
          console.error('Erro ao atualizar corrida:', error);
        }
      });
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.rideForm.controls).forEach(key => {
      const control = this.rideForm.get(key);
      control?.markAsTouched();
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.rideForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return `${this.getFieldLabel(fieldName)} é obrigatório`;
      }
      if (field.errors['min']) {
        return `${this.getFieldLabel(fieldName)} deve ser maior que ${field.errors['min'].min}`;
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      'driverId': 'Motorista',
      'date': 'Data',
      'fromCityId': 'Cidade de Origem',
      'toCityId': 'Cidade de Destino',
      'isDelivery': 'É Entrega'
    };
    return labels[fieldName] || fieldName;
  }

  onCancel(): void {
    this.router.navigate(['/rides']);
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Editar Corrida' : 'Cadastrar Corrida';
  }

  get submitButtonText(): string {
    if (this.isLoading) {
      return this.isEditMode ? 'Atualizando...' : 'Cadastrando...';
    }
    return this.isEditMode ? 'Atualizar' : 'Cadastrar';
  }
}
